import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContactDetailsTab } from "@/components/contacts/details/ContactDetailsTab";
import { ContactActivitiesTab } from "@/components/contacts/details/ContactActivitiesTab";
import { ContactConnectionsTab } from "@/components/contacts/details/ContactConnectionsTab";
import { ContactSidebar } from "@/components/contacts/details/ContactSidebar";
import { useToast } from "@/hooks/use-toast";
import { useContactOptions } from "@/hooks/useContactOptions";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { useSetAtom } from 'jotai';
import { contactsListAtom } from '@/lib/atoms';
import type { Contact } from '@/types/contact';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Share2, Play, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShareContactDialog } from "@/components/contacts/ShareContactDialog";
import { SkipContactDialog } from "@/components/contacts/SkipContactDialog";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { usePhoneCall } from "@/components/phone/PhoneCallProvider";

export function ContactDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("activities");
  const [showResubscribeDialog, setShowResubscribeDialog] = useState(false);
  const { toast } = useToast();
  const { options, loading: optionsLoading } = useContactOptions();
  const { currentWorkspace } = useWorkspaces();
  const [searchParams, setSearchParams] = useSearchParams();
  const { startCall, callState } = usePhoneCall();
  const setContactsList = useSetAtom(contactsListAtom);
  void currentWorkspace?.role; // workspace available for future role checks

  // Fetch contact data when the ID changes
  const fetchContact = useCallback(async (isInitialLoad = false) => {
    if (!id) return;

    if (isInitialLoad) setLoading(true);

    try {
      console.log('[ContactDetailsPage] Fetching contact details for ID:', id);
      
      const { data, error } = await supabase
        .from("contacts_with_primary_details")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      
      console.log('[ContactDetailsPage] Contact data fetched:', data);
      setContact(data);
    } catch (error) {
      console.error("Error fetching contact:", error);
      captureError(error as Error, { feature: 'contact-detail-fetch' });
      toast({
        title: "Error",
        description: "Failed to load contact details.",
        variant: "destructive",
      });
    } finally {
      if (isInitialLoad) setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchContact(true);
  }, [fetchContact]);

  // Refetch when a call panel that was open has just closed. Done/Next move next_contact,
  // and this page fetches imperatively (no useQuery), so the query invalidation the call
  // panel fires never reaches it — without this the header would show the new cadence next
  // to a stale date.
  const callWasOpen = useRef(false);
  useEffect(() => {
    if (callWasOpen.current && !callState.isOpen) {
      fetchContact();
    }
    callWasOpen.current = callState.isOpen;
  }, [callState.isOpen, fetchContact]);

  // Auto-trigger phone call when navigated with ?call=true&phone=...
  useEffect(() => {
    const shouldCall = searchParams.get('call') === 'true';
    const phoneParam = searchParams.get('phone');

    if (shouldCall && contact && phoneParam && !callState.isOpen) {
      // Clean up URL params first to prevent re-trigger
      setSearchParams({}, { replace: true });
      const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      startCall(contact.id, phoneParam, contactName);
      // Fire tel: link without navigating the page
      const telLink = document.createElement('a');
      telLink.href = `tel:${phoneParam}`;
      telLink.click();
    }
  }, [contact, searchParams, startCall, setSearchParams, callState.isOpen]);

  const updateContactField = async (field: keyof Contact, value: string) => {
    if (!contact || !id) return;
    
    try {
      console.log(`[ContactDetailsPage] Updating field ${field} to "${value}"`);
      
      const { error } = await supabase
        .from("contacts")
        .update({ [field]: value })
        .eq("id", id);

      if (error) throw error;

      // Update local state
      setContact(prev => prev ? { ...prev, [field]: value } : null);
      
      console.log('[ContactDetailsPage] Field updated successfully');
      
      toast({
        description: "Contact has been updated successfully.",
      });
    } catch (error) {
      console.error("Error updating contact:", error);
      captureError(error as Error, { feature: 'contact-detail-update-field' });
      toast({
        title: "Error",
        description: "Failed to update contact.",
        variant: "destructive",
      });
    }
  };

  const [remarketConfirmOpen, setRemarketConfirmOpen] = useState(false);

  // Logs a marketing-consent record as a contact activity (type 'note',
  // scheduled=false → contact history, not the calendar). Returns true on success.
  const logMarketingConsent = async (contactId: string): Promise<boolean> => {
    if (!currentWorkspace?.id) return false;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      const { error } = await supabase.from('activities').insert({
        contact_id: contactId,
        workspace_id: currentWorkspace.id,
        user_id: user.id,
        type: 'note',
        title: 'Marketing consent confirmed',
        description:
          'User confirmed they have received permission to send this contact marketing email (set ReMarket = Yes from the contact page).',
        start_date: new Date().toISOString(),
        scheduled: false,
        completed: true,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Failed to log marketing consent:', err);
      captureError(err as Error, { feature: 'marketing-consent-log' });
      return false;
    }
  };

  const handleRemarketChange = (value: string) => {
    // Setting ReMarket to Yes grants marketing consent — require an explicit
    // affirmation and log it.
    if (value === 'YES' && contact?.remarket !== 'YES') {
      setRemarketConfirmOpen(true);
      return;
    }

    // A withdrawn contact's ReMarket status is LOCKED at the database level — not just
    // against 'YES', but against any change away from 'unsubscribe'. A plain field write
    // here would be rejected with consent_locked and surface as a generic "Failed to
    // update contact". Say what is actually true instead.
    if (contact?.remarket === 'unsubscribe' || contact?.unsubscribed) {
      toast({
        title: 'This contact unsubscribed',
        description:
          "Their ReMarket status is locked because they opted out. If they've asked to receive marketing email again, use Re-enable Marketing — that records who re-subscribed them and when.",
        duration: 10000,
      });
      return;
    }

    updateContactField('remarket', value);
  };

  const confirmRemarketYes = async () => {
    if (!id) return;
    setRemarketConfirmOpen(false);

    // A contact who previously unsubscribed is a RE-subscribe, not a fresh opt-in,
    // and it must go through the doorway: only that clears the address-level
    // suppression as well as the permission flag. A plain field write here would
    // leave them passing the create gate but silently blocked at send — and once the
    // consent triggers are live it is rejected outright (consent_locked).
    if (contact?.unsubscribed || contact?.remarket === 'unsubscribe') {
      try {
        const { data: outcomes, error } = await supabase.rpc('hermes_resubscribe_contacts', {
          p_workspace_id: contact.workspace_id,
          p_contact_ids: [id],
          p_source: 'contact_detail_remarket_field',
        });
        if (error) throw error;

        const undeliverable = (outcomes ?? []).filter(
          (o: { outcome: string; is_primary: boolean }) =>
            o.outcome === 'still_undeliverable' && o.is_primary
        );
        toast(
          undeliverable.length > 0
            ? {
                title: 'Re-subscribed, but still not deliverable',
                description:
                  "Marketing is re-enabled, but this contact's email address previously bounced or was reported as spam, so it stays blocked to protect your sending reputation. Add a different email address for them.",
                duration: 30000,
              }
            : { description: 'Marketing re-enabled for this contact' }
        );
        fetchContact();
      } catch (err) {
        console.error('Error re-subscribing contact:', err);
        captureError(err as Error, { feature: 'contact-detail-remarket' });
        toast({
          title: 'Error',
          description:
            'Could not re-subscribe this contact — they are unchanged and still unsubscribed. Try again, and if it keeps failing, contact support.',
          variant: 'destructive',
        });
      }
      return;
    }

    // Never unsubscribed: an ordinary first opt-in. Record consent first, so we
    // never have remarket=YES without an audit entry.
    const logged = await logMarketingConsent(id);
    if (!logged) {
      toast({
        title: 'Error',
        description: 'Could not record marketing consent — ReMarket was not changed.',
        variant: 'destructive',
      });
      return;
    }
    await updateContactField('remarket', 'YES');
  };

  const handleDeleteContact = async () => {
    if (!id || isDeleting) return;

    setIsDeleting(true);
    try {
      console.log('Initiating contact deletion, clearing caches');
      
      // Use the safer RPC function if possible
      const { error: rpcError } = await supabase
        .rpc('delete_contacts_safely', {
          contact_ids: [id]
        }).single();
        
      if (rpcError) {
        console.warn("RPC delete failed, trying soft delete fallback:", rpcError);
        // Fallback to soft delete (set deleted_at instead of hard delete)
        const { error } = await supabase
          .from("contacts")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", id)
          .is("deleted_at", null);

        if (error) throw error;
      }

      console.log('Contact deleted successfully');
      
      toast({
        description: "Contact deleted successfully.",
      });

      // Remove from cached contacts list so it disappears immediately
      setContactsList((prev) => ({
        ...prev,
        contacts: prev.contacts.filter((c) => c.id !== id),
        totalCount: Math.max(0, prev.totalCount - 1),
      }));

      // Navigate back to contacts list
      navigate('/contacts', { replace: true });
    } catch (error) {
      console.error('Error deleting contact:', error);
      captureError(error as Error, { feature: 'contact-detail-delete' });
      toast({
        title: "Error",
        description: "Failed to delete contact.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading || optionsLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Contact not found</h2>
          <p className="text-muted-foreground mt-2">The contact you're looking for doesn't exist or has been deleted.</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate('/contacts')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] overflow-y-auto">
      <div className="flex h-full">
        {/* Left Sidebar */}
        <div className="w-80 border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          {/* Header with Back Button and Action Buttons */}
          <div className="p-4 flex items-center justify-between border-b bg-background">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="-ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowShareDialog(true)}
              >
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSkipDialog(true)}
              >
                <Play className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Contact Sidebar Content */}
          <ContactSidebar 
            contact={contact}
            onUpdate={() => {
              console.log('[ContactDetailsPage] Contact updated, refreshing data');
              fetchContact();
            }}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-background">
          <div className="p-6 space-y-6">
            {/* Contact Status Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4 rounded-lg border bg-background">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Relationship</label>
                <Select
                  value={contact.relationship || ""}
                  onValueChange={(value) => updateContactField("relationship", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.relationship.map(value => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Type</label>
                <Select
                  value={contact.type || ""}
                  onValueChange={(value) => updateContactField("type", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.type.map(value => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Rank</label>
                <Select
                  value={contact.rank || ""}
                  onValueChange={(value) => updateContactField("rank", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select rank" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.rank.map(value => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Status</label>
                <Select
                  value={contact.status || ""}
                  onValueChange={(value) => updateContactField("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.status.map(value => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">ReMarket</label>
                <Select
                  value={contact.remarket || ""}
                  onValueChange={handleRemarketChange}
                  disabled={!!contact.unsubscribed}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select remarket status" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Canonical system values — match BulkEditDialog options.
                        Legacy rows (`"true"`, `"false"`, `"Yes"`) will show no
                        selection until the user re-saves, which surfaces the
                        non-canonical value for cleanup. */}
                    <SelectItem value="YES">Yes</SelectItem>
                    <SelectItem value="NO">No</SelectItem>
                  </SelectContent>
                </Select>
                <AlertDialog open={remarketConfirmOpen} onOpenChange={setRemarketConfirmOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirm marketing permission</AlertDialogTitle>
                      <AlertDialogDescription>
                        I confirm I have received permission to send this contact marketing
                        email. This affirmation is recorded in the contact's activity history.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={confirmRemarketYes}>I confirm</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {contact.unsubscribed && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-amber-600">
                      Locked — contact unsubscribed{contact.unsubscribed_at ? ` on ${new Date(contact.unsubscribed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setShowResubscribeDialog(true)}
                    >
                      Re-enable Marketing
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList>
                <TabsTrigger value="activities">Activities</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="connections">Connections</TabsTrigger>
              </TabsList>
              
              <TabsContent value="activities">
                <ContactActivitiesTab contactId={contact.id} />
              </TabsContent>
              
              <TabsContent value="details">
                <ContactDetailsTab contactId={contact.id} onUpdate={fetchContact} />
              </TabsContent>
              
              <TabsContent value="connections">
                <ContactConnectionsTab contactId={contact.id} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Share Contact Dialog */}
      <ShareContactDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        contactId={contact.id}
        contactName={`${contact.first_name} ${contact.last_name}`}
      />

      {/* Skip Contact Dialog */}
      <SkipContactDialog
        open={showSkipDialog}
        onOpenChange={setShowSkipDialog}
        contactId={contact.id}
        onSuccess={() => {
          console.log('[ContactDetailsPage] Contact skipped, refreshing data');
          fetchContact();
        }}
      />

      {/* Delete Contact Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone
              and all data associated with this contact will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContact}
              className="bg-destructive text-destructive-foreground"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Contact"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-enable Marketing Confirmation Dialog */}
      <AlertDialog open={showResubscribeDialog} onOpenChange={setShowResubscribeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-enable Marketing for This Contact?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This contact previously unsubscribed from marketing emails. By re-enabling marketing, you confirm that:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>The contact has explicitly opted back in to receive marketing communications</li>
                <li>You have documented proof of their renewed consent</li>
                <li>You understand your obligations under CAN-SPAM, GDPR, and applicable anti-spam legislation</li>
              </ul>
              <p className="text-sm font-medium">
                This action will be logged with your identity and timestamp for compliance purposes.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, Keep Locked</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  // The re-subscribe doorway. It is the ONLY thing that clears
                  // email_address.email_status — the deliverability grain. This
                  // used to be a bare contacts.update, which cleared permission
                  // only: the contact then passed the campaign create gate, got
                  // queue jobs, and had every job killed at send with
                  // RECIPIENT_SUPPRESSED. Two real people sat silently unmailable
                  // that way for five weeks. The RPC also writes the compliance
                  // audit row inside the same transaction, so consent can no longer
                  // change without the audit landing with it.
                  const { data: outcomes, error: resubErr } = await supabase.rpc(
                    'hermes_resubscribe_contacts',
                    {
                      p_workspace_id: contact.workspace_id,
                      p_contact_ids: [contact.id],
                      p_source: 'contact_detail',
                    }
                  );
                  // supabase-js RESOLVES on a Postgres error — it does not throw —
                  // so without this check the catch below never fires and the user
                  // is told "re-enabled" when nothing was written. That was the old
                  // behavior here.
                  if (resubErr) throw resubErr;

                  // Consent is reversible; a dead mailbox is not. A bounced or
                  // spam-reported address stays blocked even now, and the remedy is
                  // a different address — so say so instead of implying success.
                  //
                  // PRIMARY only: campaigns only ever mail the primary address, so a
                  // stale bounced *alternate* row must not raise a false alarm about
                  // a contact we can mail perfectly well.
                  const undeliverable = (outcomes ?? []).filter(
                    (o: { outcome: string; is_primary: boolean }) =>
                      o.outcome === 'still_undeliverable' && o.is_primary
                  );
                  if (undeliverable.length > 0) {
                    toast({
                      title: 'Re-subscribed, but still not deliverable',
                      description:
                        "Marketing is re-enabled, but this contact's email address previously bounced or was reported as spam, so it stays blocked to protect your sending reputation. Add a different email address for them.",
                      duration: 30000,
                    });
                  } else {
                    toast({ description: 'Marketing re-enabled for this contact' });
                  }
                  fetchContact();
                } catch (err) {
                  console.error('Error re-enabling marketing:', err);
                  captureError(err as Error, { feature: 'contact-detail-remarket' });
                  toast({
                    title: "Error",
                    description: "Failed to re-enable marketing — the contact is unchanged and still unsubscribed. Try again, and if it keeps failing, contact support.",
                    variant: "destructive",
                  });
                }
              }}
            >
              Yes, Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}